import axios from 'axios'
import { MessageBox, Message } from 'element-ui'
import store from '@/store'
import router from '@/router'

// 是否显示重新登录对话框
let isReloginShowing = false
// 是否正在刷新token
let isRefreshing = false
// 失败请求队列
let requestList = []

// 创建axios的对象
const instance = axios.create({
    baseURL: "/api",  //配置固定域名
    timeout: 30000
})
 
// 请求拦截
// 所有的网络请求都会走这个方法,可以在请求添加自定义内容
instance.interceptors.request.use(
    function (config) {
        // 动态获取token
        const token = localStorage.getItem('userToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // 设置租户ID请求头
        const tenantId = store.getters.tenantId;
        if (tenantId) {
            config.headers['tenant-id'] = tenantId;
        }
        return config
    },
    function (err) {
        return Promise.reject(err)
    }
)
 
// 响应拦截
// 此处可以根据服务器返回的状态码做相应的数据
instance.interceptors.response.use(
    function (response) {
        const res = response
        console.log('=== Response Interceptor Success ===', {
            url: res.config?.url,
            status: res.status,
            data: res.data
        })
        
        // 对于刷新token接口，需要特殊处理
        if (res.config && res.config.url && res.config.url.includes('/auth/refresh-token')) {
            console.log('Refresh token response:', res.data)
            if (res.data && res.data.code === 0) {
                console.log('Refresh token success, returning data:', res.data.data)
                return res.data.data // 返回CommonResult中的data字段
            } else {
                console.error('Refresh token failed with business error:', res.data)
                // 刷新token业务失败，抛出错误
                const error = new Error('Refresh token business error')
                error.response = {
                    status: 401,
                    data: res.data
                }
                error.config = res.config
                return Promise.reject(error)
            }
        }
        
        // 检查业务状态码（仅对非刷新token接口）
        if (res.data && res.data.code === 401) {
            console.log('Business 401 error detected:', res.config.url)
            
            // 避免刷新token接口本身的401错误进入递归
            if (res.config.url.includes('/admin-api/system/auth/refresh-token') || res.config._skipAuthInterceptor) {
                console.warn('Refresh token request failed with business 401')
                handleTokenExpired()
                const error = new Error('Business 401 error')
                error.response = { status: 401, data: res.data }
                error.config = res.config
                return Promise.reject(error)
            }
            
            // 如果已经重试过，直接返回错误
            if (res.config._retry) {
                console.warn('Request already retried, handling token expired')
                handleTokenExpired()
                const error = new Error('Business 401 error')
                error.response = { status: 401, data: res.data }
                error.config = res.config
                return Promise.reject(error)
            }
            
            res.config._retry = true
            
            // 检查是否有refreshToken
            const currentRefreshToken = store.getters.refreshToken
            if (!currentRefreshToken) {
                console.warn('No refresh token available, handling token expired')
                handleTokenExpired()
                const error = new Error('Business 401 error')
                error.response = { status: 401, data: res.data }
                error.config = res.config
                return Promise.reject(error)
            }
            
            // 如果未认证，并且未进行刷新令牌，说明可能是访问令牌过期了
            if (!isRefreshing) {
                console.log('Starting token refresh process for business 401...')
                // 尝试刷新token
                return refreshAccessToken().then(newToken => {
                    console.log('Token refresh successful, retrying original request')
                    // 更新原始请求的token
                    res.config.headers.Authorization = `Bearer ${newToken}`
                    // 重新发送原始请求
                    return instance(res.config)
                }).catch((refreshError) => {
                    console.error('Token refresh failed:', refreshError)
                    // 刷新失败，跳转登录
                    const error = new Error('Business 401 error')
                    error.response = { status: 401, data: res.data }
                    error.config = res.config
                    return Promise.reject(error)
                })
            } else {
                console.log('Token refresh in progress, adding request to queue')
                // 添加到队列，等待刷新获取到新的令牌
                return new Promise((resolve, reject) => {
                    requestList.push(() => {
                        const newToken = localStorage.getItem('userToken')
                        if (newToken) {
                            res.config.headers.Authorization = `Bearer ${newToken}`
                            resolve(instance(res.config))
                        } else {
                            reject(new Error('No token available after refresh'))
                        }
                    })
                })
            }
        }
        
        return res.data
    },
    function (err) {
        console.log('=== Response Interceptor Error ===', {
            url: err.config?.url,
            status: err.response?.status,
            data: err.response?.data,
            message: err.message
        })
        
        // 处理401未授权错误
        if (err.response && err.response.status === 401) {
            console.log('=== 401 Error Debug Info ===')
            console.log('URL:', err.config.url)
            console.log('Current userToken:', localStorage.getItem('userToken'))
            console.log('Current refreshToken:', localStorage.getItem('refreshToken'))
            console.log('isRefreshing:', isRefreshing)
            console.log('Error response:', err.response.data)
            
            const originalRequest = err.config
            
            // 避免刷新token接口本身的401错误进入递归
            if (originalRequest.url.includes('/admin-api/system/auth/refresh-token') || originalRequest._skipAuthInterceptor) {
                console.warn('Refresh token request failed with 401')
                handleTokenExpired()
                return Promise.reject(err)
            }
            
            // 如果已经重试过，直接返回错误
            if (originalRequest._retry) {
                console.warn('Request already retried, handling token expired')
                handleTokenExpired()
                return Promise.reject(err)
            }
            
            originalRequest._retry = true
            
            // 检查是否有refreshToken
            const currentRefreshToken = store.getters.refreshToken
            if (!currentRefreshToken) {
                console.warn('No refresh token available, handling token expired')
                handleTokenExpired()
                return Promise.reject(err)
            }
            
            // 如果未认证，并且未进行刷新令牌，说明可能是访问令牌过期了
            if (!isRefreshing) {
                console.log('Starting token refresh process...')
                // 尝试刷新token
                return refreshAccessToken().then(newToken => {
                    console.log('Token refresh successful, retrying original request')
                    // 更新原始请求的token
                    originalRequest.headers.Authorization = `Bearer ${newToken}`
                    // 重新发送原始请求
                    return instance(originalRequest)
                }).catch((refreshError) => {
                    console.error('Token refresh failed:', refreshError)
                    // 刷新失败，跳转登录
                    return Promise.reject(err)
                })
            } else {
                console.log('Token refresh in progress, adding request to queue')
                // 添加到队列，等待刷新获取到新的令牌
                return new Promise((resolve, reject) => {
                    requestList.push(() => {
                        const newToken = localStorage.getItem('userToken')
                        if (newToken) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`
                            resolve(instance(originalRequest))
                        } else {
                            reject(new Error('No token available after refresh'))
                        }
                    })
                })
            }
        }
        return Promise.reject(err)
    }
)

// 处理请求队列
function processQueue(error, token = null) {
    console.log('Processing queue with', requestList.length, 'requests, error:', !!error, 'token:', !!token)
    requestList.forEach((cb) => {
        if (error) {
            // 如果有错误，不执行回调，让请求失败
            console.log('Skipping queued request due to error')
        } else {
            // 执行回调，重新发送请求
            console.log('Executing queued request')
            cb()
        }
    })
    
    requestList = []
}

// 刷新token
function refreshAccessToken() {
    if (isRefreshing) {
        // 如果正在刷新，将当前请求加入队列等待
        return new Promise((resolve) => {
            requestList.push(() => {
                resolve(localStorage.getItem('userToken'))
            })
        })
    }

    isRefreshing = true

    const refreshToken = store.getters.refreshToken
    const tenantId = store.getters.tenantId
    
    if (!refreshToken) {
        console.warn('No refresh token available')
        handleTokenExpired()
        return Promise.reject(new Error('No refresh token'))
    }

    console.log('Attempting to refresh token...')
    
    const params = { refreshToken }
    const headers = {}
    if (tenantId) {
        headers['tenant-id'] = tenantId
    }
    
    return instance.post('/admin-api/system/auth/refresh-token', null, {
        params: params,
        headers: headers,
        _skipAuthInterceptor: true // 标记跳过401拦截
    }).then(response => {
        console.log('Raw refresh token response:', response)
        // 响应拦截器已经处理了response.data，这里response就是AuthLoginRespVO对象
        if (response && response.accessToken) {
            console.log('Token refresh successful, new token:', response.accessToken)
            console.log('New refresh token:', response.refreshToken)
            
            // 更新localStorage中的token
            localStorage.setItem('userToken', response.accessToken)
            localStorage.setItem('refreshToken', response.refreshToken)
            
            // 更新Vuex store
            store.dispatch('login', {
                token: response.accessToken,
                refreshToken: response.refreshToken,
                userType: store.getters.userType,
                userId: response.userId || store.getters.userId,
                tenantId: response.tenantId || store.getters.tenantId,
                userInfo: store.getters.userInfo
            })
            
            // 处理队列中的请求
            processQueue(null, response.accessToken)
            return response.accessToken
        } else {
            console.error('Refresh token response invalid:', response)
            throw new Error('Refresh failed - no accessToken in response')
        }
    }).catch(error => {
        console.error('Token refresh failed:', error)
        // 刷新失败，处理队列但不重试
        processQueue(error, null)
        handleTokenExpired()
        throw error
    }).finally(() => {
        console.log('Token refresh process completed, resetting state')
        isRefreshing = false
    })
}

// 处理token过期
function handleTokenExpired() {
    if (isReloginShowing) {
        return; // 避免重复显示对话框
    }
    
    isReloginShowing = true;
    
    // 立即清除登录状态
    store.dispatch('logout');
    
    // 如果当前在个人中心页面，跳转到首页
    if (router.currentRoute.path === '/profile') {
        router.push('/home');
    }
    
    // 显示登录过期提示并弹出登录框
    Message.warning('登录已过期，请重新登录');
    
    // 通过全局事件通知App.vue显示登录弹窗
    window.dispatchEvent(new CustomEvent('token-expired-show-login'));
    
    isReloginShowing = false;
}
 
// 封装get和post请求
export function get(url, params) {
    return instance.get(url, {params})
}
 
export function post(url, data) {
    return instance.post(url, data)
}
 
export default instance;